<script setup lang="ts">
import { computed } from 'vue';
import { PropertyPurpose } from '@/helpers/const';
import IncomingPropertyReceiveRentalIncome from '@/components/application/incoming-property-receive-rental-income.vue';
import IncomingPropertyExpectedRentalIncome from '@/components/application/incoming-property-expected-rental-income.vue';
import IncomingPropertyIsNewBuild from '@/components/application/incoming-property-is-new-build.vue';
import IncomingPropertyContractExchangedBeforeMay2026 from '@/components/application/incoming-property-contract-exchanged-before-may-2026.vue';
import { useNegativeGearingFy27Asset } from '@/helpers/useNegativeGearingFy27Asset';
import type { PropertyPurposeType } from '@/types/api';

const props = defineProps<{
  id: string;
  propertyPurpose?: PropertyPurposeType;
  receiveRentalIncome?: boolean;
  isNewBuild?: boolean;
}>();

const showRentalIncomeQuestion = computed(() => props.propertyPurpose === PropertyPurpose.INVESTMENT_PROPERTY);

const { showNewBuild, showContractExchange } = useNegativeGearingFy27Asset(
  computed(() => props.propertyPurpose),
  computed(() => props.isNewBuild),
);
</script>

<template>
  <div class="mt-6">
    <IncomingPropertyReceiveRentalIncome
      v-if="showRentalIncomeQuestion"
      :id="`${id}-rental-income-question`"
      :data-test-id="`${id}-rental-income-question`"
      label="Does this property generate rental income?"
    />
    <IncomingPropertyExpectedRentalIncome
      v-if="showRentalIncomeQuestion && receiveRentalIncome"
      :id="`${id}-rental-income`"
      :data-test-id="`${id}-rental-income`"
    />
    <IncomingPropertyIsNewBuild v-if="showNewBuild" :id="id" />
    <IncomingPropertyContractExchangedBeforeMay2026 v-if="showContractExchange" :id="id" />
    <BxField v-slot="{ field }" name="outstandingLoanAmount">
      <BxNumberInput
        v-bind="field"
        :id="`${id}-outstanding-mortgage`"
        :data-test-id="`${id}-outstanding-mortgage`"
        label="Outstanding Mortgage"
        label-type="floating"
        leading-symbol="$"
        class="form-control"
        help-text="Please enter zero if there is no outstanding mortgage."
      />
    </BxField>
    <BxField v-slot="{ field }" name="supportingPropertyValue">
      <BxNumberInput
        v-bind="field"
        :id="`${id}-supporting-property-value`"
        :data-test-id="`${id}-supporting-property-value`"
        class="form-control"
        label="Estimated Valuation"
        label-type="floating"
        leading-symbol="$"
      />
    </BxField>
  </div>
</template>
